import React, { useRef, useState } from "react";
import { Divider, IconButton, Menu } from "@mui/material";
import { FavoriteOutlined, FavoriteBorderOutlined, Forum, MoreVert } from "@mui/icons-material";
import {
    Avatar, Box, CustomMenuItem, Typography,
    VideoPlayer, useCommonComponentContext,
} from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import CommentInPost, { ModeCallBackComment } from "./commentInPost";
import UserReactionService from "../../services/message/UserReactionService";
import CommunityPostService from "../../services/message/CommunityPost.service";

import { renderTime } from "../BellNotification";
import { IUserComment } from "../../commons/interfaces";
import { IRecordPost } from "../../containers/post/usePost";
import { ICommunityPostDetail } from "../../containers/channel/useChannel";
import { DiscussionGroupMemberRole, DiscussionGroupType } from "../../constants/enums";
import { convertDataCommentToIlatestComment, convertIlatestCommentToDataComment } from "../Channel/convertData";



export const getNameRoleCommunity = (value: number) => {
    let name = "";
    if (DiscussionGroupMemberRole.Member === value) {
        name = "Member";
    }
    if (DiscussionGroupMemberRole.Moderator === value) {
        name = "Moderator";
    }
    if (DiscussionGroupMemberRole.Owner === value) {
        name = "Owner";
    }
    return name;
}

const ItemDetailPostComment = (props: {
    isDetail: boolean;
    dataDetail?: ICommunityPostDetail;

    onEditPost?: (data: IRecordPost) => void;
    onShowUserProfile?: (data: IUserComment) => void;
    onClickBox?: (data: ICommunityPostDetail) => void;
    onCallBack?: (data: ICommunityPostDetail, isDelete: boolean) => void;
}) => {

    const Strings = useStrings();

    const {
        clientId,
        language,
        userInfo,
        onError,
        onSuccess,
        onHideLoading,
        onShowLoading,
    } = useCommonComponentContext();

    const anchorElIconMenu = useRef<HTMLButtonElement>(null);

    const [openPopupMenu, setOpenPopupMenu] = useState(false);

    // close popup menu more
    const handleClose = (event: React.MouseEvent<EventTarget>) => {
        if (anchorElIconMenu.current && anchorElIconMenu.current.contains(event.target as HTMLElement)) {
            return;
        }
        setOpenPopupMenu(false);
    };

    const handleFollowPost = async (idPost: string) => {
        try {
            onShowLoading();

            await CommunityPostService.follow(idPost);

            let dataTemp = { ...props.dataDetail };
            dataTemp.isFollowed = !dataTemp.isFollowed;

            Helpers.isFunction(props.onCallBack) &&
                props.onCallBack(dataTemp, false);

            onHideLoading();
        } catch (error) {
            onHideLoading();
            const err = Helpers.renderExceptionError(error);
            onError(err);
        }
    }

    const handleUnfollowPost = async (idPost: string) => {
        try {
            onShowLoading();

            await CommunityPostService.unFollow(idPost);

            let dataTemp = { ...props.dataDetail };
            dataTemp.isFollowed = !dataTemp.isFollowed;

            Helpers.isFunction(props.onCallBack) &&
                props.onCallBack(dataTemp, false);

            onHideLoading();
        } catch (error) {
            onHideLoading();
            const err = Helpers.renderExceptionError(error);
            onError(err);
        }
    }

    const handleUnReaction = async () => {
        try {
            onShowLoading();

            const idReaction = props.dataDetail?.currentReaction?.id || "";

            await UserReactionService.unReacted({ id: idReaction });

            let dataTemp = { ...props.dataDetail };
            dataTemp.isReacted = !dataTemp.isReacted;
            dataTemp.totalReact = dataTemp.totalReact - 1;
            dataTemp.currentReaction = undefined;

            Helpers.isFunction(props.onCallBack) &&
                props.onCallBack(dataTemp, false);

            onHideLoading();
        } catch (error) {
            onHideLoading();
            const err = Helpers.renderExceptionError(error);
            onError(err);
        }
    }

    const handleReaction = async () => {
        try {
            onShowLoading();

            const reactionCode = 2;

            const data = {
                clientId: clientId,
                targetId: props.dataDetail?.id,
                reactionCode: reactionCode,
                targetType: DiscussionGroupType.ThreadPost,
                userId: userInfo?.userProfile?.identityId || "",
                organizationId: userInfo?.userProfile?.organizationId,
            };

            const result = await UserReactionService.reaction(data);

            let dataTemp = { ...props.dataDetail };
            dataTemp.isReacted = !dataTemp.isReacted;
            dataTemp.totalReact = dataTemp.totalReact + 1;
            dataTemp.currentReaction = result;

            Helpers.isFunction(props.onCallBack) &&
                props.onCallBack(dataTemp, false);

            onHideLoading();
        } catch (error) {
            onHideLoading();
            const err = Helpers.renderExceptionError(error);
            onError(err);
        }
    }

    const handleDeletePost = (idPost: string) => {
        Helpers.showConfirmAlert(
            Strings.CHANNEL.CONFIRM_DELETE_POST_IN_CHANNEL_SUCCESS,
            async () => {
                try {
                    onShowLoading();

                    await CommunityPostService.delete(idPost);

                    Helpers.isFunction(props.onCallBack) &&
                        props.onCallBack(props.dataDetail, true);

                    onSuccess(Strings.CHANNEL.DELETE_POST_IN_CHANNEL_SUCCESS);

                    onHideLoading();
                } catch (error) {
                    onHideLoading();
                    const err = Helpers.renderExceptionError(error);
                    onError(err);
                }
            }
        );
    }

    const handleEditPost = () => {
        Helpers.isFunction(props.onEditPost) &&
            props.onEditPost({
                tenantCode: props.dataDetail?.tenantCode,
                organizationId: props.dataDetail?.organizationId,
                id: props.dataDetail?.id,
                title: props.dataDetail?.title,
                content: props.dataDetail?.content,
                shortDescription: props.dataDetail?.shortDescription,
                postTag: props.dataDetail?.postTag,
                eventTime: props.dataDetail?.eventTime,
                channelId: props.dataDetail?.parentGroupId,
                postImages: props.dataDetail?.postImages,
                postVideos: props.dataDetail?.postVideos,
                resourceCode: props.dataDetail?.resourceCode,
                publishedTime: props.dataDetail?.publishedTime,
                publishedStatus: props.dataDetail?.publishedStatus,
                status: props.dataDetail?.status,
                createTime: props.dataDetail?.createTime,
                createUser: props.dataDetail?.createUser,
                updateTime: props.dataDetail?.updateTime,
                updateUser: props.dataDetail?.updateUser,
            });
    }

    const renderIconPopupMenuMore = () => {
        return (
            <div style={{ display: "contents" }}>
                <IconButton
                    edge="start"
                    aria-haspopup="true"
                    aria-label="icon_more"
                    ref={anchorElIconMenu}
                    aria-controls={openPopupMenu ? "icon_menu_more" : undefined}
                    onClick={async () => {
                        setOpenPopupMenu(prevOpen => !prevOpen);
                    }}
                >
                    <MoreVert />
                </IconButton>

                <Menu
                    id="icon_menu_more"
                    open={openPopupMenu}
                    anchorReference={null}
                    anchorEl={anchorElIconMenu.current}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    onClose={handleClose}
                >
                    <Box minWidth="150px">
                        {
                            props.dataDetail?.isFollowed
                                ? (
                                    <CustomMenuItem
                                        key={"unfollow"}
                                        colorText="dark"
                                        title={Strings.CHANNEL.UNFOLLOW.toLocaleUpperCase()}
                                        onClick={() => {
                                            props.dataDetail?.id && handleUnfollowPost(props.dataDetail?.id);
                                            setOpenPopupMenu(prevOpen => !prevOpen);
                                        }}
                                    />
                                )
                                : (
                                    <CustomMenuItem
                                        key={"follow"}
                                        colorText="dark"
                                        title={Strings.CHANNEL.FOLLOW.toLocaleUpperCase()}
                                        onClick={() => {
                                            props.dataDetail?.id && handleFollowPost(props.dataDetail?.id);
                                            setOpenPopupMenu(prevOpen => !prevOpen);
                                        }}
                                    />
                                )
                        }
                        {/* <CustomMenuItem
                            key={"pin"}
                            colorText="dark"
                            title={Strings.CHANNEL.PIN.toLocaleUpperCase()}
                            onClick={() => {
                                setOpenPopupMenu(prevOpen => !prevOpen);
                            }}
                        /> */}

                        {/* is Post Owner */}
                        {
                            (userInfo?.userProfile?.identityId === props.dataDetail?.owner?.userId) &&
                            <CustomMenuItem
                                key={"edit"}
                                colorText="info"
                                title={Strings.Common.EDIT.toLocaleUpperCase()}
                                onClick={() => {
                                    props.dataDetail?.id && handleEditPost();
                                    setOpenPopupMenu(prevOpen => !prevOpen);
                                }}
                            />
                        }
                        {/* is Post Owner */}
                        {
                            !props.isDetail &&
                            (userInfo?.userProfile?.identityId === props.dataDetail?.owner?.userId) &&
                            <CustomMenuItem
                                key={"delete"}
                                colorText="error"
                                title={Strings.Common.DELETE.toLocaleUpperCase()}
                                onClick={() => {
                                    props.dataDetail?.id && handleDeletePost(props.dataDetail?.id);
                                    setOpenPopupMenu(prevOpen => !prevOpen);
                                }}
                            />
                        }
                    </Box>
                </Menu>
            </div>
        );
    }

    return (
        <Box sx={{ backgroundColor: "#FFFFFF", p: "16px 20px" }} >
            {/* POST OWNER */}
            <Box sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        "&:hover": {
                            cursor: "pointer",
                        }
                    }}
                    onClick={() => {
                        props.onShowUserProfile &&
                            props.onShowUserProfile({
                                role: props.dataDetail?.owner?.role,
                                userId: props.dataDetail?.owner?.userId,
                                userName: props.dataDetail?.owner?.userName,
                                fullName: props.dataDetail?.owner?.fullName,
                                avatarId: props.dataDetail?.owner?.avatarId,
                                avatarUrl: props.dataDetail?.owner?.avatarUrl,
                            });
                    }}
                >
                    <Avatar
                        style={{ border: "1px #344767 solid" }}
                        src={props.dataDetail?.owner?.avatarUrl}
                        text={props.dataDetail?.owner?.fullName || props.dataDetail?.owner?.userName}
                    />

                    <Box mx={2} display="flex" flexDirection="column" justifyContent="space-between">
                        <Typography variant="h6" color="dark" fontWeight="bold" sx={{
                            maxWidth: "200px",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                        }}>
                            {props.dataDetail?.owner?.fullName || "User Name"}
                        </Typography>
                        <Typography variant="button" color="secondary" fontWeight="regular">
                            {renderTime(Number(props.dataDetail?.publishedTime || "0"))}
                        </Typography>
                    </Box>

                    <Box>
                        <Box mt="4px" px={1} py={0.25} display="flex" borderRadius="4px" bgColor="#b5d0f375">
                            <Typography variant="caption" color="info" fontWeight="bold">
                                {getNameRoleCommunity(props.dataDetail?.owner?.role || 0)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box display="flex" alignItems="center">
                    {
                        props.dataDetail?.isFollowed &&
                        <Box mr="10px" px={1} py={0.5} display="flex" borderRadius="8px" bgColor="info">
                            <Typography variant="caption" color="white" fontWeight="bold">
                                {Strings.CHANNEL.FOLLOW}
                            </Typography>
                        </Box>
                    }
                    {renderIconPopupMenuMore()}
                </Box>
            </Box>
            <Divider />
            {/*  */}

            {/* Content Post */}
            <Box
                sx={{
                    mt: "16px",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                        cursor: !props.isDetail ? "pointer" : "none",
                    }
                }}
                onClick={() => {
                    (!props.isDetail && props.onClickBox) &&
                        props.onClickBox(props.dataDetail);
                }}
            >
                <Typography variant="button" color="dark" fontWeight="bold">
                    {props.dataDetail?.title?.value?.[language] || ""}
                </Typography>

                <Typography sx={{ mt: "0.5rem" }} variant="button" color="dark" fontWeight="regular">
                    {props.dataDetail?.content?.value?.[language] || ""}
                </Typography>

                {
                    props.dataDetail?.postImages?.length > 0 &&
                    <Box mt={1} display="flex" justifyContent="center">
                        {
                            props.dataDetail?.postImages?.map((item) => (
                                <img
                                    width={100}
                                    alt={item.fileId}
                                    src={item.fileUrl}
                                />
                            ))
                        }
                    </Box>
                }
                {
                    props.dataDetail?.postVideos?.length > 0 &&
                    <Box mt={1} display="flex" justifyContent="center">
                        {
                            props.dataDetail?.postVideos?.map(item => (
                                <VideoPlayer
                                    key={item.fileId}
                                    fileId={item.fileId}
                                />
                            ))
                        }
                    </Box>
                }
            </Box>
            {/*  */}

            {/* Action Post */}
            <Box mt={2} mb={1} display="flex" alignItems="center" >
                <IconButton sx={{ p: 0 }} onClick={() => {
                    props.dataDetail?.isReacted
                        ? handleUnReaction()
                        : handleReaction()
                }}>
                    <Box display="flex" alignItems="center">
                        {
                            props.dataDetail?.isReacted
                                ? <FavoriteOutlined />
                                : <FavoriteBorderOutlined />
                        }
                        &nbsp;
                        <Typography variant="caption" color="dark" fontWeight="regular">
                            {props.dataDetail?.totalReact || "0"}
                        </Typography>
                    </Box>
                </IconButton>

                <IconButton sx={{ p: 0, ml: 2 }} onClick={() => {
                    (!props.isDetail && props.onClickBox) &&
                        props.onClickBox(props.dataDetail);
                }}>
                    <Box display="flex" alignItems="center">
                        <Forum />&nbsp;
                        <Typography variant="caption" color="dark" fontWeight="regular">
                            {`${props.dataDetail?.totalComment || "0"} ${Strings.CHANNEL.COMMENTS.toLocaleUpperCase()}`}
                        </Typography>
                    </Box>
                </IconButton>
            </Box>
            <Divider />
            {/*  */}

            {/* Comment in Post by User login  */}
            <Box mt={2}>
                <CommentInPost
                    requestLevel={3}
                    isDetail={props.isDetail}
                    threadId={props.dataDetail?.threadId}
                    channelId={props.dataDetail?.channelId}
                    dataComments={convertIlatestCommentToDataComment(props.dataDetail?.latestComment)}
                    onCallBack={(data) => {
                        let dataTemp = { ...props.dataDetail };

                        if (data.mode === ModeCallBackComment.Create) {
                            dataTemp.totalComment = dataTemp.totalComment + 1;
                        }
                        if (data.mode === ModeCallBackComment.Delete) {
                            dataTemp.totalComment = dataTemp.totalComment - 1;
                        }

                        dataTemp.latestComment = convertDataCommentToIlatestComment(data.firstComment)[0];

                        Helpers.isFunction(props.onCallBack) &&
                            props.onCallBack(dataTemp, false);
                    }}
                />
            </Box>
            {/*  */}
        </Box>
    );
};

export default ItemDetailPostComment;
